import React, { useContext } from 'react';
import { RegularButton as Button, Skeleton } from 'fronton-react';
import { ArrowRightIcon } from '@fronton/icons-react';
import type { TBasketItem } from 'pages/calculation/types/basket-item.type';
import { getUpdatedBasket } from 'pages/calculation/helpers/get-updated-basket.helper';
import { useCalculationState } from 'pages/calculation/store/calculation';
import { DataContext } from 'entries/common/widgets/context';
import { gaAddToCart } from 'ga-events/ga-add-to-cart';
import { gaClickOnGoods } from 'ga-events/ga-click-goods';
import { dimensionsAtom } from 'pages/dimensions/store/dimensions';
import { useRecoilValue } from 'recoil';
import { getComplimentaryProductInfoForBasket } from 'pages/calculation/helpers/get-complimentary-product-info-for-basket.helper';
import { addComplimentaryProductsToBasketArr } from 'pages/calculation/helpers/add-complimentary-products-to-basket-arr.helper';
import { Snackbar } from 'pages/common/components/Snackbar';
import { getAllProductsInBasket } from 'pages/calculation/helpers/get-all-products-in-basket.helper';
import { getProductQuantityInBasket } from '../../../helpers/get-product-quantity-in-basket.helper';
import { goToCartButtonClass } from './cart.styles';
import { useBasketState, useBasketUpdate } from '../../../store/basket';

const BASKET_URL = 'basket';

type TCartComponentProps = {
  productId: string;
  count: number;
};

export const CartComponent: React.FC<TCartComponentProps> = ({ productId, count }) => {
  const data = useContext(DataContext);

  const { calculationFormState, setCalculationState } = useCalculationState();
  const { addToCartIsSuccessful } = calculationFormState;
  const { basket, isBasketLoading, loadBasket, updateBasket, addProductsToBasket } = useBasketState();

  useBasketUpdate(loadBasket, updateBasket);

  const productQuantityInBasket = getProductQuantityInBasket(basket, productId);
  const dimensions = useRecoilValue(dimensionsAtom);
  const complimentaryProductInfoForBasket = getComplimentaryProductInfoForBasket(dimensions);

  const basketArr: TBasketItem[] = getUpdatedBasket(basket, {
    code: productId,
    quantity: productQuantityInBasket + count,
  });

  const complementaryProductsWithBasketProducts = addComplimentaryProductsToBasketArr({
    basketArr,
    complimentaryProductInfoForBasket,
  });

  const handleClick = async () => {
    const allProductsInBasket = getAllProductsInBasket({ dimensions, data, count });
    addProductsToBasket(complementaryProductsWithBasketProducts);
    allProductsInBasket.forEach(item =>
      gaAddToCart({
        id: item.productId,
        quantity: item.productQuantity,
        price: item.price ?? 0,
        name: item.name ?? '',
      })
    );
    setCalculationState(prevState => ({
      ...prevState,
      addToCartIsSuccessful: true,
    }));
  };

  const onClickBasket = () => {
    gaClickOnGoods({
      title: 'Добавлено в корзину',
      name: 'Перейти в корзину',
    });

    window.open(`${window?.location?.origin}/${BASKET_URL}/`, '_blank', 'noopener noreferrer');
  };

  return (
    <>
      {addToCartIsSuccessful ? (
        <Button
          data-testid="calculation_go_to_cart_button"
          className={goToCartButtonClass}
          size="l"
          variant="primary"
          onClick={onClickBasket}
          withExtraClickZone
          iconRight={<ArrowRightIcon type="outline" size="l" color="text-invert" />}>
          Корзина
        </Button>
      ) : (
        <Skeleton variant="control" showLoading={isBasketLoading}>
          <Button
            size="l"
            variant="primary"
            label="В корзину"
            type="submit"
            form="calculation-form"
            onClick={handleClick}>
            В корзину
          </Button>
        </Skeleton>
      )}
      <Snackbar />
    </>
  );
};
